/**
 * Фильтрация заблокированных кандидатов перед созданием рассылки (TASKNEIROKLYUCH-434).
 *
 * Матчинг через композитный ключ `hhResumeId | phone | email` — устойчиво к E-Staff
 * кандидатам, добавленным только по контактам (без hhResumeId). Раньше использовался
 * матчинг через `availableIds` по hhResumeId — терял такие записи в filter(Boolean).
 *
 * Инверсия: исключаем blocked вместо включения available, так как blocked-payload
 * гарантированно содержит hhResumeId + контакты (см. relevanter-back/routes/ai-recruiter.js).
 */

export interface FullCandidate {
  id?: string | number | null;
  phone?: string | null;
  email?: string | null;
  [key: string]: unknown;
}

export interface BlockedCandidate {
  externalId?: string | number | null;
  hhResumeId?: string | number | null;
  phone?: string | null;
  email?: string | null;
  name?: string;
  reason?: string;
  source?: 'estaff' | 'estaff_error' | 'internal' | 'hh' | string;
}

export interface CheckResult {
  blocked?: BlockedCandidate[] | null;
}

function makeKey(hhResumeId: unknown, phone: unknown, email: unknown): string {
  const id = hhResumeId == null ? '' : String(hhResumeId);
  const p = phone == null ? '' : String(phone);
  const e = email == null ? '' : String(email);
  return `${id}|${p}|${e}`;
}

export function filterBlockedCandidates<T extends FullCandidate>(
  validCandidates: T[],
  result: CheckResult,
): T[] {
  const blocked = result.blocked || [];
  const blockedKeys = new Set(blocked.map((b) => makeKey(b.hhResumeId, b.phone, b.email)));
  // REL-326: externalId — точный ключ кандидата в рассылке (source-мапперы кладут туда
  // String(candidate.id)). Композитный ключ остаётся фолбэком: его по-прежнему хватает
  // потокам, которые externalId не шлют (отклики, clarify-experience).
  const blockedExternalIds = new Set(
    blocked
      .map((b) => (b.externalId == null ? null : String(b.externalId)))
      .filter((id): id is string => id != null && id !== ''),
  );
  if (blockedKeys.size === 0 && blockedExternalIds.size === 0) return validCandidates;
  return validCandidates.filter((c) => {
    const externalId = c.id == null ? null : String(c.id);
    if (externalId && blockedExternalIds.has(externalId)) return false;
    return !blockedKeys.has(makeKey(c.id, c.phone, c.email));
  });
}
